import {
  buildGameTitle,
  legalMoves,
  deriveConnect4,
  DEFAULT_GUEST_NAME,
  type Connect4Move,
  type GameKind,
  type GameMeta,
} from '@awwyeah/shared';

/**
 * Pure(-ish) mutation/derivation helpers for the `games` registry and a match's move log —
 * copied shape from `apps/web/src/modules/apps/appToolOps.ts`, minus the audience machinery
 * games don't have (see `packages/shared/src/modules/games/index.ts`'s doc comment).
 */

export function newId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function createGameMeta(input: {
  kind: GameKind;
  starterId: string;
  starterName: string;
  opponentId: string;
  opponentName: string;
}): GameMeta {
  const at = nowIso();
  return {
    id: newId(),
    kind: input.kind,
    title: buildGameTitle(input.starterName, input.opponentName),
    playerIds: [input.starterId, input.opponentId],
    // No invite/accept step in v1 — picking an opponent from the roster starts the match
    // immediately (the one "Dan started a game with you" push covers the notice).
    status: 'active',
    createdBy: input.starterId,
    createdAt: at,
    updatedBy: input.starterId,
    updatedAt: at,
  };
}

/**
 * A game whose second seat is a guest play link (WS-23).
 *
 * Two differences from `createGameMeta`, and both are the point:
 *
 *  - `status: 'open'` — the state `GAME_STATUSES` has carried unused since WS-17a finally
 *    means something: seat 2 exists but nobody is sitting in it. A member's board shows the
 *    link panel while it holds; the first thing the guest's arrival does is flip it to
 *    `'active'` (`guestSeat.ts`).
 *  - the id is passed in, not generated here, because it was already spent on the mint call
 *    that allocated `guestId` — the link and the game have to agree on which game it is.
 *
 * The title says "Dan vs Guest" until the guest picks a name; the name they choose is
 * mirrored into `guestNames`, and every surface resolves display names through that rather
 * than through the frozen title.
 */
export function createGuestGameMeta(input: {
  id: string;
  kind: GameKind;
  starterId: string;
  starterName: string;
  guestId: string;
}): GameMeta {
  const at = nowIso();
  return {
    id: input.id,
    kind: input.kind,
    title: buildGameTitle(input.starterName, DEFAULT_GUEST_NAME),
    playerIds: [input.starterId, input.guestId],
    status: 'open',
    createdBy: input.starterId,
    createdAt: at,
    updatedBy: input.starterId,
    updatedAt: at,
  };
}

/**
 * Client-side pre-check before pushing a move — the server-equivalent replay
 * (`deriveConnect4`) is the real authority and re-validates everything anyway, but an honest
 * client should never emit a move it already knows is illegal (out of turn, full column,
 * game already decided).
 */
export function canPlayMove(
  movesSoFar: readonly Connect4Move[],
  playerIds: readonly [string, string],
  userId: string,
  col: number,
): boolean {
  const state = deriveConnect4(movesSoFar, playerIds);
  if (state.turn === null) return false;
  if (playerIds[state.turn] !== userId) return false;
  return legalMoves(state.board).includes(col);
}
